/**
 * useFlowSignal.ts -- MER Algo Footprints v9.2.0
 *
 * React hook that fetches recent trades from Polygon and runs the
 * institutionalFlowEngine to produce a FlowSignal for the given symbol.
 *
 * Phase 1 Session D: debug-panel consumer. Later phases (confluence,
 * thesis) will integrate this differently -- likely through a shared
 * subscription rather than per-component fetching.
 *
 * Design notes:
 *
 *   - Polls every POLL_MS. Default 60s; matches the institutional timescale
 *     and stays well under Polygon Advanced tier rate limits.
 *
 *   - WINDOW_MS = last 5 minutes. Short enough to be responsive, long
 *     enough to capture meaningful institutional patterns.
 *
 *   - Visibility-gated: when document.hidden goes true, poll pauses.
 *     Resumes on visibility change. Matches the liveQuoteStream pattern.
 *
 *   - Market-hours check: skips fetch when isMarketDataAvailable() is
 *     false. Avoids burning API calls overnight / weekends.
 *
 *   - Symbol-null safe: returns inert state when symbol is null/empty.
 *
 *   - fetchTrades returns null on failure; we surface that as an error
 *     state without throwing.
 */

import { useEffect, useState, useCallback } from 'react';
import { fetchTrades } from '../domains/marketData/providers/polygonProvider';
import { computeFlow } from '../domains/institutionalFlow/engine/computeFlow';
import { computeClusters } from '../domains/institutionalFlow/clusters';
import type { FlowSignal } from '../domains/institutionalFlow/schemas';
import { isMarketDataAvailable } from '../shared/hooks/useMarketData';
import {
  setLiveFlowContext,
  clearLiveFlowContext,
  setLivePrintClusters,
  clearLivePrintClusters,
} from '../domains/ai/prompts/promptBuilder';

const POLL_MS   = 60_000;
const WINDOW_MS = 5 * 60_000;

export interface UseFlowSignalResult {
  signal:      FlowSignal | null;
  loading:     boolean;
  lastUpdated: number | null;
  error:       string | null;
}

export function useFlowSignal(symbol: string | null | undefined): UseFlowSignalResult {
  const [signal, setSignal]           = useState<FlowSignal | null>(null);
  const [loading, setLoading]         = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [error, setError]             = useState<string | null>(null);

  const fetchOnce = useCallback(async (sym: string) => {
    if (!isMarketDataAvailable()) {
      // Markets closed; leave existing signal in place rather than nulling.
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const now    = Date.now();
      const fromMs = now - WINDOW_MS;
      const result = await fetchTrades(sym, fromMs, now);
      if (result === null) {
        setError('Failed to fetch trades');
        return;
      }
      const next = computeFlow(sym, result.trades, fromMs, now, result.partial);
      setSignal(next);
      setLastUpdated(now);
      // Phase 1 v1 integration: feed signal into the AI thesis sidecar so
      // the next thesis generation can reference institutional flow context.
      setLiveFlowContext(sym, next);
      // Phase 2 v1 (liquidity multi-source): compute off-exchange print
      // clusters from the same trade window and feed them to the cluster
      // sidecar. liquidityEngine reads from this to boost zones with
      // dark-pool support. Pure transformation; no extra fetch.
      const clusters = computeClusters(sym, result.trades, fromMs, now, result.partial);
      setLivePrintClusters(sym, clusters);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!symbol) {
      setSignal(null);
      setLastUpdated(null);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    // Fire one immediately
    void fetchOnce(symbol);

    timer = setInterval(() => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchOnce(symbol);
    }, POLL_MS);

    // Resume on visibility return
    const onVis = () => {
      if (!document.hidden && !cancelled) void fetchOnce(symbol);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
      // Clear sidecar entries so a previous symbol's data doesn't leak
      // into a future thesis or liquidity computation for a different
      // symbol.
      if (symbol) {
        clearLiveFlowContext(symbol);
        clearLivePrintClusters(symbol);
      }
    };
  }, [symbol, fetchOnce]);

  return { signal, loading, lastUpdated, error };
}
